import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Gift, Copy, Check, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ReferralCard — shows "your invite code + link", accepts a redeem code once,
 * shows tally of qualified / pending referrals.
 */
export default function ReferralCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redeemCode, setRedeemCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [redeeming, setRedeeming] = useState(false);

  const auth = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/referrals/me`, { headers: auth() });
      setData(res.data);
    } catch (_e) {
      // silent — not critical
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const share = () => {
    if (!data?.code) return;
    const url = `${window.location.origin}/login?ref=${encodeURIComponent(data.code)}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success("Referral link copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const redeem = async () => {
    if (!redeemCode.trim()) return;
    setRedeeming(true);
    try {
      const res = await axios.post(
        `${API}/referrals/redeem`,
        { code: redeemCode.trim().toUpperCase() },
        { headers: auth() },
      );
      toast.success(`Applied! You'll get ${res.data.reward_days_per_side} free days when you upgrade.`);
      setRedeemCode("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not apply code");
    } finally {
      setRedeeming(false);
    }
  };

  if (loading) return null;
  if (!data?.code) return null;

  return (
    <Card className="bg-gradient-to-br from-amber-500/10 via-slate-900 to-slate-900 border-amber-500/20" data-testid="referral-card">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
            <Gift className="w-4 h-4 text-amber-400" />
          </div>
          <CardTitle className="text-lg text-slate-100">Give a month, get a month</CardTitle>
        </div>
        <CardDescription className="text-slate-400 text-sm">
          Share SlideCon. When your friend upgrades to any paid plan, you both get <b>{data.reward_days_per_side} free days</b>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="flex-1 bg-slate-950/60 border border-slate-800 rounded-lg px-3 py-2 font-mono text-amber-300 text-lg tracking-wider" data-testid="referral-code">
            {data.code}
          </div>
          <Button
            variant="outline"
            className="border-slate-700"
            onClick={share}
            data-testid="referral-share-btn"
          >
            {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
            {copied ? "Copied" : "Copy link"}
          </Button>
        </div>

        <div className="flex flex-wrap gap-2 text-xs">
          <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-300" data-testid="referral-qualified-badge">
            <Sparkles className="w-3 h-3 mr-1" />
            {data.qualified} qualified
          </Badge>
          <Badge variant="outline" className="border-slate-700 text-slate-400">
            {data.pending} pending
          </Badge>
          <Badge variant="outline" className="border-slate-700 text-slate-400">
            {data.total_rewarded_months} months earned
          </Badge>
        </div>

        {/* Redeem someone else's code (once) */}
        {data.qualified === 0 && data.pending === 0 && (
          <div className="pt-3 border-t border-slate-800">
            <div className="text-xs text-slate-400 mb-2">Got a code from a friend?</div>
            <div className="flex gap-2">
              <Input
                placeholder="FRIEND-CODE"
                value={redeemCode}
                onChange={(e) => setRedeemCode(e.target.value)}
                className="bg-slate-950/60 border-slate-800 font-mono"
                data-testid="referral-redeem-input"
              />
              <Button
                onClick={redeem}
                disabled={redeeming || !redeemCode.trim()}
                variant="outline"
                className="border-slate-700"
                data-testid="referral-redeem-btn"
              >
                {redeeming ? "..." : "Apply"}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
